import { PaasTransaction, SideDrawer, TrendLineChart, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import type { EnrichedReports } from '@/hooks/useApi/useFetchEnrichedReports';
import MetricTooltip from '../MetricTooltip';
import useMetricTooltips from '../useMetricTooltips';
import CategoryEditor from '../CategoryEditor/CategoryEditor';
import { formatAmount } from '../formatAmount';
import { filterRowsByCategory, formatMonthKey, formatTransactionDate, rowAmount } from '../insightsModel';
import type { CategoryDescriptor } from '../categories';
import styles from './CategoryDrawer.module.scss';

const cn = withStyles(styles);

type CategoryDrawerProps = {
  descriptor: CategoryDescriptor | null;
  categoryLabel: string;
  reports: EnrichedReports;
  transactions: PaasTransaction[];
  months: string[];
  currency: string;
  isEnhanced: boolean;
  categories: CategoryDescriptor[];
  onClose: () => void;
  onUpdateCategory: (transactionID: string, classification: string) => Promise<void>;
};

export default function CategoryDrawer({
  descriptor,
  categoryLabel,
  reports,
  transactions,
  months,
  currency,
  isEnhanced,
  categories,
  onClose,
  onUpdateCategory,
}: CategoryDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.DataEnrichment.Drawer' });
  const tooltips = useMetricTooltips();

  const report = descriptor ? reports.categories[descriptor.reportType] : null;
  const filteredRows = descriptor ? filterRowsByCategory(transactions, descriptor, months) : [];

  const points = months.map((monthKey) => ({
    label: formatMonthKey(monthKey),
    value: report?.SumPerMonth[monthKey] ?? 0,
  }));

  const counts = months.map((monthKey) => report?.CountPerMonth[monthKey] ?? 0);

  const totalAmount = points.reduce((sum, point) => sum + point.value, 0);
  const numTransactions = counts.reduce((sum, count) => sum + count, 0);

  const newestDate = filteredRows[0]?.TransactionDate.slice(0, 10);
  const oldestDate = filteredRows[filteredRows.length - 1]?.TransactionDate.slice(0, 10);
  const lastTwoDates = filteredRows.slice(0, 2).map((row) => row.TransactionDate.slice(0, 10));

  return (
    <SideDrawer isOpen={descriptor !== null} onClose={onClose} title={categoryLabel}>
      {descriptor && (
        <div className={cn('container')}>
          <div className={cn('topRow')}>
            <div className={cn('graphCard')}>
              <div className={cn('graphStats')}>
                <div>
                  <div className={cn('statLabel')}>{t('totalAmount', { defaultValue: 'Total Amount' })}</div>
                  <div className={cn('statValue')}>{formatAmount(totalAmount, currency)}</div>
                </div>

                <div>
                  <div className={cn('statLabel')}>
                    {t('totalTransactions', { defaultValue: 'Total Transactions' })}
                  </div>
                  <div className={cn('statValue')}>{numTransactions}</div>
                </div>
              </div>

              <TrendLineChart
                points={points}
                formatValue={(value) => formatAmount(value, currency)}
                getTooltipLines={(index) => [
                  t('tooltipTransactions', {
                    defaultValue: `${counts[index]} transactions`,
                    count: counts[index],
                  }),
                  formatAmount(points[index].value, currency),
                ]}
              />
            </div>

            <div className={cn('detailsCard')}>
              <div className={cn('detailRow')}>
                <span className={cn('detailLabel')}>
                  {t('avgCategory', { defaultValue: `Avg. ${categoryLabel}`, category: categoryLabel })}
                </span>

                <span className={cn('detailValue')}>
                  {formatAmount(months.length > 0 ? totalAmount / months.length : 0, currency)}
                </span>
              </div>

              <div className={cn('detailRow')}>
                <span className={cn('detailLabel')}>
                  {t('avgDaysBetween', { defaultValue: 'Avg. Days Between' })}
                  {isEnhanced && <MetricTooltip information={tooltips.avgDaysBetween} />}
                </span>

                <span className={cn('detailValue')}>
                  {report?.AverageDaysBetween !== null && report?.AverageDaysBetween !== undefined
                    ? t('days', {
                        defaultValue: `${report.AverageDaysBetween.toFixed(1)} days`,
                        days: report.AverageDaysBetween.toFixed(1),
                      })
                    : '—'}
                </span>
              </div>

              <div className={cn('detailRow')}>
                <span className={cn('detailLabel')}>
                  {t('firstTransaction', { defaultValue: 'First Transaction' })}
                </span>
                <span className={cn('detailValue')}>{oldestDate ?? '—'}</span>
              </div>

              <div className={cn('detailRow')}>
                <span className={cn('detailLabel')}>{t('lastTransaction', { defaultValue: 'Last Transaction' })}</span>
                <span className={cn('detailValue')}>{newestDate ?? '—'}</span>
              </div>

              <div className={cn('detailRow', 'alignTop')}>
                <span className={cn('detailLabel')}>
                  {t('lastTwoDates', { defaultValue: 'Last Two Payment Dates' })}
                </span>

                <span className={cn('detailValue')}>
                  {lastTwoDates.length > 0
                    ? // Index in the key: two payments can land on the same date
                      lastTwoDates.map((date, index) => <div key={`${date}-${index}`}>{date}</div>)
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          <div className={cn('tableWrap')}>
            <div className={cn('tableHeader')}>
              <span>{t('columnDate', { defaultValue: 'Transaction Date' })}</span>
              <span>{t('columnDescription', { defaultValue: 'Description' })}</span>
              <span>{t('columnAmount', { defaultValue: 'Amount' })}</span>
              <span>{t('columnCategory', { defaultValue: 'Category' })}</span>
            </div>

            <div className={cn('tableBody')}>
              {filteredRows.map((row) => (
                <div key={row.AggregatorTransactionID} className={cn('tableRow')}>
                  <span>{formatTransactionDate(row.TransactionDate)}</span>
                  <span>{row.Description}</span>
                  <span>{formatAmount(rowAmount(row, descriptor), currency)}</span>

                  <CategoryEditor
                    transactionID={row.AggregatorTransactionID}
                    classification={row.Classification}
                    side={descriptor.side}
                    categories={categories}
                    onUpdateCategory={onUpdateCategory}
                  />
                </div>
              ))}

              {filteredRows.length === 0 && (
                <p className={cn('emptyState')}>
                  {t('noTransactions', { defaultValue: 'No transactions in this period.' })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </SideDrawer>
  );
}
